import { motion } from 'framer-motion'
import { useState } from 'react'
import { MODE_META } from '@clicklinx/protocol'
import { Icon } from '../components/Icon'
import { ShapeGlyph } from '../components/Shape'
import { Button, CoinChip, Panel, cx } from '../components/ui'
import { formatCents } from '../lib/payments'
import { useAuth } from '../lib/auth'
import { formatDuration } from '../lib/format'
import { SEAT_COLORS, initialsOf, seatWash } from '../lib/seats'
import { useGame } from '../lib/socket'

export function LobbyScreen() {
  const { user } = useAuth()
  const { room, leaveRoom, setReady, startMatch, updateConfig, connected } = useGame()
  const [copied, setCopied] = useState(false)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!room || !user) return null

  const me = room.players.find((p) => p.userId === user.id)
  const isHost = me?.isHost ?? false
  const everyoneReady = room.players.every((p) => p.ready)
  const enoughPlayers = room.players.length >= 2
  const emptySeats = Math.max(0, room.config.maxPlayers - room.players.length)
  const isPrize = room.config.purse == 'prize'

  const copyInvite = async () => {
    const link = `${window.location.origin}/?join=${room.code}`
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2_000)
    } catch {
      setError('Could not copy. Share the code instead.')
    }
  }

  const start = async () => {
    setError(null)
    setStarting(true)
    try {
      await startMatch()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not start the match.')
    } finally {
      setStarting(false)
    }
  }

  return (
    <div className="min-h-dvh pad-safe pad-safe-top">
      <header className="mx-auto flex w-full max-w-2xl items-center justify-between py-4">
        <button
          type="button"
          onClick={() => void leaveRoom()}
          className="flex h-11 items-center gap-2 rounded-xl pr-3 text-sm text-fg-muted transition-colors hover:text-fg"
        >
          <Icon name="arrowLeft" size={18} />
          Leave
        </button>
        {isPrize ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-lime/30 bg-lime/10 px-2.5 py-1 font-mono text-xs font-medium tnum text-lime">
            <Icon name="trophy" size={13} />
            {formatCents(user.prizeCoins)}
          </span>
        ) : (
          <CoinChip amount={user.balance} tone="gold" size="sm" />
        )}
      </header>

      <main className="mx-auto w-full max-w-2xl pb-14">
        {/* The code is the whole point of this screen, so it gets the space. */}
        <Panel className="mb-5 p-6 text-center sm:p-8">
          <p className="eyebrow">Room code</p>
          <p className="mt-3 font-mono text-[clamp(2.5rem,13vw,4rem)] font-bold leading-none tracking-[0.16em] tnum text-lime text-glow">
            {room.code}
          </p>

          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button variant="outline" size="sm" onClick={() => void copyInvite()}>
              <Icon name={copied ? 'check' : 'copy'} size={16} />
              {copied ? 'Link copied' : 'Copy invite link'}
            </Button>
          </div>
        </Panel>

        {/* Match settings */}
        <Panel flush className="mb-5 p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="display-title text-sm text-fg">{MODE_META[room.config.mode].name}</p>
              <p className="mt-1.5 text-xs text-fg-muted">{MODE_META[room.config.mode].tagline}</p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1.5 text-fg-muted">
                <Icon name="clock" size={15} />
                <span className="font-mono tnum">{formatDuration(room.config.duration)}</span>
              </span>
              <span className="flex items-center gap-1.5 text-fg-muted">
                <Icon name="users" size={15} />
                <span className="font-mono tnum">
                  {room.players.length}/{room.config.maxPlayers}
                </span>
              </span>
            </div>
          </div>

          <div className="hairline my-4" />

          <div className="flex items-center justify-between">
            <span className="text-sm text-fg-muted">Ante each</span>
            {isPrize ? (
              <span className="font-mono text-sm font-medium tnum text-fg">
                {formatCents(room.config.stake)}
              </span>
            ) : (
              <CoinChip amount={room.config.stake} size="sm" />
            )}
          </div>
          {/* Seats, changeable in place.
              The player count is chosen before the room exists, and its default
              is a duel — so a host who invites two friends discovers the limit
              only when the second is refused, by which point the room has to be
              abandoned and remade. Raising it here costs one tap. */}
          {isHost && (
            <div className="mt-2.5 flex items-center justify-between">
              <span className="text-sm text-fg-muted">Seats</span>
              <div className="flex rounded-lg border border-ink-700 p-0.5">
                {[2, 3, 4].map((seats) => {
                  const tooFew = seats < room.players.length
                  return (
                    <button
                      key={seats}
                      type="button"
                      disabled={tooFew}
                      onClick={() => void updateConfig({ ...room.config, maxPlayers: seats })}
                      className={cx(
                        // 44px square: these were 31x24, which is a miss on a
                        // phone and the only way to change the room size.
                        'flex h-11 w-11 items-center justify-center rounded-[6px] font-mono text-sm font-medium tnum transition-colors',
                        room.config.maxPlayers === seats
                          ? 'bg-ink-750 text-fg'
                          : tooFew
                            ? 'text-fg-dim/40'
                            : 'text-fg-dim hover:text-fg-muted',
                      )}
                      // A seat count below the people already here would evict
                      // somebody, so the server refuses it and so does this.
                      title={tooFew ? 'More players are already here' : undefined}
                    >
                      {seats}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          <div className="mt-2.5 flex items-center justify-between">
            <span className="text-sm text-fg-muted">Winner takes</span>
            {isPrize ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-lime/30 bg-lime/10 px-2.5 py-1 font-mono text-xs font-medium tnum text-lime">
                {formatCents(room.config.stake * room.players.length)}
              </span>
            ) : (
              <CoinChip amount={room.config.stake * room.players.length} tone="gold" size="sm" />
            )}
          </div>
        </Panel>

        {/* Seats */}
        <div className="mb-6 space-y-2.5">
          {room.players.map((player, index) => (
            <motion.div
              key={player.userId}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05, duration: 0.24 }}
              className="flex items-center gap-3.5 rounded-2xl border border-ink-700 bg-ink-900 p-3.5"
            >
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-mono text-sm font-bold"
                style={{
                  background: seatWash(player.color, 0.16),
                  color: SEAT_COLORS[player.color],
                  boxShadow: `inset 0 0 0 1px ${seatWash(player.color, 0.35)}`,
                }}
              >
                {initialsOf(player.displayName)}
              </span>

              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 truncate text-sm font-semibold text-fg">
                  {player.userId === user.id ? 'You' : player.displayName}
                  {player.isHost && <Icon name="crown" size={14} className="shrink-0 text-gold" />}
                </p>
                <p className="mt-0.5 text-xs text-fg-dim">
                  {!player.connected ? 'Reconnecting…' : player.isHost ? 'Host' : 'Player'}
                </p>
              </div>

              <span
                className={cx(
                  'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.6875rem] font-semibold uppercase tracking-wider',
                  player.ready ? 'bg-lime-wash text-lime' : 'bg-ink-800 text-fg-dim',
                )}
              >
                {player.ready && <Icon name="check" size={12} />}
                {player.ready ? 'Ready' : 'Waiting'}
              </span>
            </motion.div>
          ))}

          {/* Empty seats are shown, not hidden, so the host can see what they
              are still waiting for. */}
          {Array.from({ length: emptySeats }).map((_, index) => (
            <div
              key={`empty-${index}`}
              className="flex items-center gap-3.5 rounded-2xl border border-dashed border-ink-700/70 p-3.5"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ink-900 text-fg-dim">
                <ShapeGlyph shape="circle" size={13} />
              </span>
              <p className="text-sm text-fg-dim">Waiting for a player…</p>
            </div>
          ))}
        </div>

        {error && (
          <p role="alert" className="mb-4 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            {error}
          </p>
        )}

        {/* Actions */}
        {isHost ? (
          <div className="space-y-3">
            <Button
              size="lg"
              fullWidth
              loading={starting}
              disabled={!enoughPlayers || !everyoneReady || !connected}
              onClick={() => void start()}
            >
              Start match
              <Icon name="arrowRight" size={18} />
            </Button>
            {!enoughPlayers && (
              <p className="text-center text-xs text-fg-dim">
                Share the code — you need at least one opponent.
              </p>
            )}
            {enoughPlayers && !everyoneReady && (
              <p className="text-center text-xs text-fg-dim">
                Waiting for everyone to mark themselves ready.
              </p>
            )}
          </div>
        ) : (
          <Button
            size="lg"
            fullWidth
            variant={me?.ready ? 'outline' : 'primary'}
            onClick={() => setReady(!me?.ready)}
          >
            {me?.ready ? 'Not ready' : "I'm ready"}
            {!me?.ready && <Icon name="check" size={18} />}
          </Button>
        )}
      </main>
    </div>
  )
}
